// Choose an album's cover from the photos already in it (W39.54).
//
// The row this opens used to be dead on screen: 「選擇封面照片」 had no
// `onClick`, rendered with the same weight and the same `›` as the two rows
// beside it that worked, and tapping it left the DOM byte-identical
// (21384 → 21384). It could not simply be wired up, because the cover was a
// *derived* value — four server-side call sites recomputed it to the newest
// photo on every upload and delete, and `AlbumUpdate` had no field for a
// choice. So this ships with the server contract it needs
// (`cover_media_id` + `cover_is_manual`, migration `x9y0z1a2b3c4`).
//
// Two things this deliberately does *not* do:
//
//   * It does not offer photos from other albums. The server rejects those
//     (422 `COVER_NOT_IN_ALBUM`) because a cover pointing outside the album
//     renders as a broken tile on the list screen. Offering them would only
//     produce a tap that fails.
//   * It does not re-fetch. The album detail page already holds every page of
//     items, so passing them in keeps the sheet instant and means the cover can
//     be chosen from a photo the user can see the title of.

import { useState } from "react";
import { Sparkles } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { mediaFileUrl } from "@/lib/api/album";
import type { AlbumItemRead } from "@/lib/api/album";

export interface AlbumCoverSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every loaded item of this album, in display order. */
  items: readonly AlbumItemRead[];
  /** The current cover, so the chosen one is marked. */
  currentCoverMediaId: string | null;
  coverIsManual: boolean;
  busy?: boolean;
  /** Returned value is ignored; only rejection is handled. */
  onPick: (mediaId: string) => unknown;
  /** 「自動封面」 — hand the cover back to the newest photo. */
  onUseAutomatic: () => unknown;
  onClose: () => void;
}

export function AlbumCoverSheet({
  open,
  onOpenChange,
  items,
  currentCoverMediaId,
  coverIsManual,
  busy = false,
  onPick,
  onUseAutomatic,
  onClose,
}: AlbumCoverSheetProps) {
  const [error, setError] = useState<string | null>(null);
  const photos = items.filter((it) => it.media.mime_type.startsWith("image/"));

  const run = async (action: () => unknown) => {
    setError(null);
    try {
      await action();
      onClose();
    } catch (err) {
      // The sheet stays open on failure. Closing it would throw away the user's
      // position in a long list of photos, which is the whole cost of the
      // action; the message says what happened and the choices are still there.
      setError(err instanceof Error ? err.message : "設定封面失敗，請稍後再試。");
    }
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} variant="mobile" title="選擇封面照片">
      <div className="flex flex-col gap-4" data-testid="album-cover-sheet">
        {photos.length === 0 ? (
          <p className="text-body-sm text-ink-2" data-testid="album-cover-empty">
            這本相簿還沒有照片，先新增相片才能選封面。
          </p>
        ) : (
          <>
            <button
              type="button"
              disabled={busy}
              data-testid="album-cover-automatic"
              onClick={() => void run(onUseAutomatic)}
              className="press flex min-h-[44px] w-full items-center gap-s-3 rounded-2xl bg-paper-elevated px-s-4 py-s-3 text-left text-body text-ink-1 hover:bg-black/5 transition-colors disabled:opacity-40"
            >
              <Sparkles size={20} className="text-gold-ink" aria-hidden />
              <span className="flex-1">
                自動封面
                <span className="block text-body-xs text-ink-3">永遠用最新上傳的那張</span>
              </span>
              {!coverIsManual ? (
                <span className="text-body-xs text-you-strong" data-testid="album-cover-automatic-current">
                  目前使用中
                </span>
              ) : null}
            </button>

            <div className="grid grid-cols-3 gap-1" data-testid="album-cover-grid">
              {photos.map((item, idx) => {
                // Marked from `currentCoverMediaId` alone, **not** from
                // `coverIsManual`. When the cover is automatic, the server's
                // newest photo is still the cover the user is looking at on the
                // list screen, and gating the badge on `coverIsManual` left
                // every tile unmarked — so the picker could not tell you which
                // one it currently is. The wording says which kind it is
                // instead. (Found by `proof-trash-and-cover.mjs`, which picked
                // "a photo that is not the current cover" and got the very photo
                // that was current.)
                const isCurrent = item.media.id === currentCoverMediaId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={busy}
                    data-testid={`album-cover-option-${item.media.id}`}
                    data-current={isCurrent ? "true" : "false"}
                    aria-pressed={isCurrent}
                    aria-label={`用第 ${idx + 1} 張照片當封面`}
                    onClick={() => void run(() => onPick(item.media.id))}
                    className="press relative aspect-square overflow-hidden rounded-md bg-paper-deep disabled:opacity-40"
                  >
                    <img
                      src={mediaFileUrl(item.media.id)}
                      alt={item.caption ?? ""}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                    {isCurrent ? (
                      <span
                        className="absolute bottom-0 left-0 right-0 bg-ink-1/75 px-1.5 py-0.5 text-center text-body-xs text-paper"
                        data-testid="album-cover-current-badge"
                      >
                        {coverIsManual ? "目前封面" : "目前封面（自動）"}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {error ? (
          <p className="text-body-sm text-partner-strong" data-testid="album-cover-error" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => {
            setError(null);
            onOpenChange(false);
          }}
          className="press min-h-[44px] w-full rounded-full border border-hairline text-body text-ink-1 hover:bg-black/5 transition-colors"
        >
          取消
        </button>
      </div>
    </BottomSheet>
  );
}
